import {useAuth} from '@common/auth/use-auth';
import {Subscription} from '@common/billing/subscription';
import {isRunningTrialSubscription} from '@common/billing/trials/is-trial-subscription';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TrialDaysLeft {
  trial: Subscription;
  daysLeft: number;
}

/**
 * Calendar days, in the viewer's time zone, from today to the day the trial
 * ends: 0 = it ends today, 1 = it ends tomorrow. Counting whole 24-hour blocks
 * instead would say "tomorrow" on the trial's last day. Never negative; null
 * when the date cannot be parsed.
 */
export function getTrialDaysLeft(
  trialEndsAt: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!trialEndsAt) return null;
  const endsAt = new Date(trialEndsAt);
  if (Number.isNaN(endsAt.getTime())) return null;
  if (endsAt.getTime() <= now) return 0;
  const today = new Date(now);
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  ).getTime();
  const startOfEndDay = new Date(
    endsAt.getFullYear(),
    endsAt.getMonth(),
    endsAt.getDate(),
  ).getTime();
  // round, not floor: a daylight-saving day is 23 or 25 hours long
  return Math.max(0, Math.round((startOfEndDay - startOfToday) / DAY_MS));
}

/**
 * The current user's running free trial and how many days of it are left.
 * Null for everyone who is not on a trial (guests, paying subscribers,
 * cancelled-but-in-grace subscribers).
 */
export function useTrialDaysLeft(): TrialDaysLeft | null {
  const {user, isOnTrial} = useAuth();
  if (!isOnTrial) return null;

  // access lasts until the trial that ends last
  let result: TrialDaysLeft | null = null;
  for (const sub of user?.subscriptions ?? []) {
    if (!sub.valid || !isRunningTrialSubscription(sub)) continue;
    const daysLeft = getTrialDaysLeft(sub.trial_ends_at ?? sub.ends_at);
    if (daysLeft != null && (!result || daysLeft > result.daysLeft)) {
      result = {trial: sub, daysLeft};
    }
  }
  return result;
}
